import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import ajax from '@/lib/ajax';
import store from '@/store';
import { AppModule } from '@/store/Auth/AppModule';
import { AuthModule } from '@/store/Auth/AuthModule';
import { DepositAccountSettingsModule } from '@/store/Accounts/DepositAccountSettingsModule';
import FilterAccountsRequest from '@/models/Shared/FilterAccountsRequest';
import AdjustmentRequest from '@/models/Accounts/DepositAccounts/Tools/AdjustmentRequest';
import RollForwardAccountDateRequest from '@/models/Accounts/DepositAccounts/Tools/RollForwardAccountDateRequest';
import DepositAccount from '@/models/Accounts/DepositAccounts/DepositAccount';
import DepositAccountSummary from '@/models/Accounts/DepositAccounts/DepositAccountSummary';
import UpdateAccountRequest from '@/models/Accounts/Shared/UpdateAccountRequest';
import UpdateAccountStateRequest from '@/models/Accounts/Shared/UpdateAccountStateRequest';
import AccountSearchType from '@/models/Shared/AccountSearchType';
import Wallet from '@/models/Accounts/DepositAccounts/Wallets/Wallet';
import CreateWalletRequest from '@/models/Accounts/DepositAccounts/Wallets/CreateWalletRequest';
import ManuallyPostInterestRequest from '@/models/Accounts/DepositAccounts/Wallets/ManuallyPostInterestRequest';
import UpdateWalletDetailsRequest from '@/models/Accounts/DepositAccounts/Wallets/UpdateWalletDetailsRequest';
import UpdateSecondaryWalletInterestSettingsRequest from '@/models/Accounts/DepositAccounts/Wallets/UpdateSecondaryWalletInterestSettingsRequest';
import CreateWalletTransferRequest from '@/models/Accounts/DepositAccounts/Wallets/CreateWalletTransferRequest';
import DebitCreditCode from '@/models/Shared/DebitCreditCode';
import Card from '@/models/Accounts/Shared/Cards/Card';
import CreateCardRequest from '@/models/Accounts/Shared/Cards/CreateCardRequest';
import UpdateCardRequest from '@/models/Accounts/Shared/Cards/UpdateCardRequest';
import Payee from '@/models/Accounts/Shared/Payees/Payee';
import PaymentOrder from '@/models/Accounts/Shared/PaymentOrders/PaymentOrder';
import CreatePayeeRequest from '@/models/Accounts/Shared/Payees/CreatePayeeRequest';
import CreatePaymentOrderRequest from '@/models/Accounts/Shared/PaymentOrders/CreatePaymentOrderRequest';
import CancelRejectPaymentOrderRequest from '@/models/Accounts/Shared/PaymentOrders/CancelRejectPaymentOrderRequest';
import Payment from '@/models/Accounts/Shared/Payments/Payment';
import PaymentState from '@/models/Accounts/Shared/Payments/PaymentState';
import ProductSubType from '@/models/Products/ProductSubType';
import Mandate from '@/models/Accounts/DepositAccounts/Mandates/Mandate';
import Fee from '@/models/Accounts/Shared/Fees/Fee';
import CreateFeeRequest from '@/models/Accounts/Shared/Fees/CreateFeeRequest';
import Transaction from '@/models/Accounts/Shared/Transactions/Transaction';
import TransactionType from '@/models/Accounts/Shared/Transactions/TransactionType';
import TransactionsOrderOptions from "@/models/Accounts/Shared/Transactions/TransactionsOrderOptions";
import BuySellFXRequest from '@/models/Accounts/DepositAccounts/Wallets/BuySellFXRequest';
import HistoryEntry from '@/models/Shared/HistoryEntry';
import AccountType from '@/models/Shared/AccountType';
import GetHistoryResponse from '@/models/Shared/GetHistoryResponse';
import Product from '@/models/Products/Product';
import ProductIssue from '@/models/Products/ProductIssues/ProductIssue';
import CurrencyUtils from "@/lib/currencyUtils";
import PermissionUtils from '@/lib/permissionUtils';
import ValidationUtils from '@/lib/validationUtils';
import Amount from '@/models/Shared/Amount';

export interface IDepositAccountState {
    loadingDepositAccounts: boolean;
    loadingDepositAccount: boolean;
    loadingWallets: boolean;
    loadingCards: boolean;
    loadingDialog: boolean;
    loadingHistory: boolean;
    loadingTransactions: boolean;
    loadingPendingTransactions: boolean;
    loadingPaymentOrders: boolean;
    loadingPayees: boolean;
    loadingPaymentsForPaymentOrder: boolean;
    loadingPayments: boolean;
    loadingMandates: boolean;
    loadingFees: boolean;
    depositAccount: DepositAccount | null;
    totalDepositAccountsPages: number;
    depositAccounts: DepositAccountSummary[];
    depositAccountsSearch: string;
    depositAccountsSearchType: AccountSearchType;
    currentDepositAccountWallet: Wallet;
    currentDepositAccountWalletTransactions: Transaction[];
    currentDepositAccountWalletTransactionRichData: Transaction;
    totalDepositAccountWalletTransactionsPages: number;
    currentDepositAccountWalletPendingTransactions: Transaction[];
    totalDepositAccountWalletPendingTransactionsPages: number;
    currentDepositAccountWalletTransactionsOrder: TransactionsOrderOptions;
    currentDepositAccountWalletTransactionsSearch: string;
    cards: Card[];
    totalCardsPages: number;
    currentCard: Card;
    payees: Payee[];
    payeesForDialog: Payee[];
    totalPayeesPages: number;
    currentPayee: Payee;
    paymentOrders: PaymentOrder[];
    totalPaymentOrdersPages: number;
    currentPaymentOrder: PaymentOrder;
    paymentsForPaymentOrder: Payment[];
    payments: Payment[];
    totalPaymentsPages: number;
    currentPayment: Payment;
    filterByPaymentState: PaymentState;
    mandates: Mandate[];
    mandate: Mandate;
    totalMandatePages: number;
    fees: Fee[];
    totalFeesPages: number;
    currentFee: Fee;
    productOptions: Product[];
    productIssueOptions: ProductIssue[];
    history: HistoryEntry[];
    pageSize: number;
}

@Module({ dynamic: true, store, name: 'DepositAccountModule', namespaced: true })
class DepositAccountStore extends VuexModule implements IDepositAccountState {

    public loadingDepositAccounts: boolean = true;
    public loadingDepositAccount: boolean = true;
    public loadingWallets: boolean = true;
    public loadingCards: boolean = true;
    public loadingDialog: boolean = false;
    public loadingHistory: boolean = true;
    public loadingTransactions: boolean = true;
    public loadingPendingTransactions: boolean = true;
    public loadingPaymentOrders: boolean = true;
    public loadingPayees: boolean = true;
    public loadingPaymentsForPaymentOrder: boolean = true;
    public loadingPayments: boolean = true;
    public loadingMandates: boolean = true;
    public loadingFees: boolean = true;
    public depositAccount: DepositAccount = new DepositAccount();
    public totalDepositAccountsPages: number = 1;
    public depositAccounts: DepositAccountSummary[] = [];
    public depositAccountsSearch: string = '';
    public depositAccountsSearchType: AccountSearchType = AccountSearchType.AccountName;
    public currentDepositAccountWallet: Wallet = new Wallet();
    public currentDepositAccountWalletTransactions: Transaction[] = []
    public currentDepositAccountWalletTransactionRichData: Transaction = new Transaction();
    public totalDepositAccountWalletTransactionsPages: number = 1;
    public currentDepositAccountWalletPendingTransactions: Transaction[] = [];
    public totalDepositAccountWalletPendingTransactionsPages: number = 1;
    public currentDepositAccountWalletTransactionsOrder: TransactionsOrderOptions = TransactionsOrderOptions.DisplayDate;
    public currentDepositAccountWalletTransactionsSearch: string = '';
    public cards: Card[] = [];
    public totalCardsPages: number = 1;
    public currentCard: Card = new Card();
    public payees: Payee[] = [];
    public payeesForDialog: Payee[] = [];
    public totalPayeesPages: number = 1;
    public currentPayee: Payee = new Payee();
    public paymentOrders: PaymentOrder[] = [];
    public totalPaymentOrdersPages: number = 1;
    public currentPaymentOrder: PaymentOrder = new PaymentOrder();
    public paymentsForPaymentOrder: Payment[] = [];
    public payments: Payment[] = [];
    public totalPaymentsPages: number = 1;
    public currentPayment: Payment = new Payment();
    public filterByPaymentState: PaymentState = PaymentState.Completed;
    public mandates: Mandate[] = [];
    public mandate: Mandate = new Mandate;
    public totalMandatePages: number = 1;
    public fees: Fee[] = [];
    public totalFeesPages: number = 1;
    public currentFee: Fee = new Fee();
    public productOptions: Product[] = [];
    public productIssueOptions: ProductIssue[] = [];
    public history: HistoryEntry[] = [];
    public pageSize: number = PermissionUtils.getPageSize();

    @Action
    async getDepositAccounts(request: FilterAccountsRequest) {

        this.setLoadingDepositAccounts(true);

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {

            request.pageSize = this.pageSize;

            // If there is a search value, apply the search parameters here depending on the search type
            if (this.depositAccountsSearch) {
                if (this.depositAccountsSearchType === AccountSearchType.AccountName) {
                    request.name = this.depositAccountsSearch;
                }
                else if (this.depositAccountsSearchType === AccountSearchType.AccountIdentifier) {
                    request.paymentAccountIdentifier = this.depositAccountsSearch;
                }
                else {
                    request.customerName = this.depositAccountsSearch;
                }
            }

            await ajax.post('/portal-backend/ux-api/customer-accounts', request)
            // TODO: Swap to the below GET API once the account number has been added to the reponse (or we don't need account number on the customer accounts list)
            // await ajax.get(`/portal-backend/ux-api/accounts?pageSize=${this.pageSize}&pageNumber=${request.pageNumber}${searchParameter}`)
            .then(response => {
                if (response.data.accounts) {
                    this.context.commit("setDepositAccounts", response.data.accounts);
                }
                else {
                    this.context.commit("resetDepositAccounts");
                }
                this.context.commit("setDepositAccountsTotalPages", response.data.records.totalRecords);
                this.setLoadingDepositAccounts(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDepositAccounts(false);
            });
        }
        // Else get Accounts from FI instead
        else {

            // If there is a search value, apply the search parameters here depending on the search type
            let searchParameter = '';
            let encodedDepositAccountsSearch = '';
            if (this.depositAccountsSearch) {
                encodedDepositAccountsSearch = ValidationUtils.encodeRFC5987ValueChars(this.depositAccountsSearch);
                if (this.depositAccountsSearchType === AccountSearchType.AccountName) {
                    searchParameter = `&name=${encodedDepositAccountsSearch}`;
                }
                else if (this.depositAccountsSearchType === AccountSearchType.AccountIdentifier) {
                    searchParameter = `&paymentAccountIdentifier=${encodedDepositAccountsSearch}`;
                }
                else {
                    searchParameter = `&customerName=${encodedDepositAccountsSearch}`;
                }
            }

            await ajax.get(`/portal-backend/fi-api/accounts?pageSize=${this.pageSize}&pageNumber=${request.pageNumber}${searchParameter}`)
            .then(response => {
                if (response.data.results) {
                    this.context.commit("setDepositAccounts", response.data.results);
                }
                else {
                    this.context.commit("resetDepositAccounts");
                }
                this.context.commit("setDepositAccountsTotalPages", response.data.records.totalRecords);
                this.setLoadingDepositAccounts(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDepositAccounts(false);
            });
        }
    }

    @Action
    async updateDepositAccountsSearch(searchText: string) {
        // For a smoother UI experience we immidiately set the loading flag
        // and then reset the deposit accounts and page count all before
        // searching, this makes the UI look faster and avoids old data hanging around
        this.setLoadingDepositAccounts(true);
        this.context.commit("resetDepositAccounts");
        this.context.commit("setDepositAccountsTotalPages", 1);
        // Then set the search text
        this.context.commit("setDepositAccountsSearch", searchText);
    }

    @Action
    async updateDepositAccountsSearchType(searchType: AccountSearchType) {
        this.context.commit("setDepositAccountsSearchType", searchType);
    }

    @Action
    async getDepositAccount(id: string) {

        // Reset deposit account to blank to avoid showing previously loaded account data
        this.context.commit("setDepositAccount", new DepositAccount());

        this.setLoadingDepositAccount(true);

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.get(`/portal-backend/ux-api/accounts/${id}`)
            .then(response => {
                this.context.commit("setDepositAccount", response.data);
                this.setLoadingDepositAccount(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDepositAccount(false);
            });
        }
        // Else get Customer Accounts from FI instead
        else {
            await ajax.get(`/portal-backend/fi-api/accounts/${id}`)
            .then(response => {
                this.context.commit("setDepositAccount", response.data);
                this.setLoadingDepositAccount(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDepositAccount(false);
            });
        }
    }

    @Action
    async editDepositAccount(request: UpdateAccountRequest) {

        this.setLoadingDialog(true);

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.patch(`/portal-backend/ux-api/accounts/${this.depositAccount.id}`, request)
            .then(response => {
                this.context.commit("setDepositAccount", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
        // Else get Customer Accounts from FI instead
        else {
            await ajax.patch(`/portal-backend/fi-api/accounts/${this.depositAccount.id}`, request)
            .then(response => {
                this.context.commit("setDepositAccount", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
    }

    @Action
    async updateDepositAccountState(request: UpdateAccountStateRequest) {

        this.setLoadingDialog(true);

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.patch(`/portal-backend/ux-api/accounts/${this.depositAccount.id}/state`, request)
            .then(response => {
                this.context.commit("setDepositAccount", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
        // Else get Customer Accounts from FI instead
        else {
            await ajax.patch(`/portal-backend/fi-api/accounts/${this.depositAccount.id}/state`, request)
            .then(response => {
                this.context.commit("setDepositAccount", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
    }

    @Action
    async createDepositAccountWallet(request: CreateWalletRequest) {

        this.setLoadingDialog(true);

        request.accountId = this.depositAccount.id;

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.post(`/portal-backend/ux-api/accounts/${this.depositAccount.id}/wallets`, request)
            .then(response => {
                this.context.commit("setDepositAccountWallet", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
        // Else create wallet in FI instead
        else {
            await ajax.post(`/portal-backend/fi-api/wallets`, request)
            .then(response => {
                this.context.commit("setDepositAccountWallet", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
    }

    @Action
    async createNewWalletTransfer(request: CreateWalletTransferRequest) {

        this.setLoadingDialog(true);

        request.amount.amount = CurrencyUtils.convertDecimalStringToAmountString(request.amount.amount);
        request.amount.currency = this.currentDepositAccountWallet.balance.currency;

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.post(`/portal-backend/ux-api/wallets/${this.currentDepositAccountWallet.id}/transfer`, request)
            .then(response => {
                this.context.commit("setDepositAccountWalletsAfterTransfer", response.data.sourceWallet);
                this.context.commit("setDepositAccountWalletsAfterTransfer", response.data.destinationWallet);

                // Only get an updated list of transactions after the wallet transfer returns successfully
                this.getWalletTransactions({id: this.currentDepositAccountWallet.id, page: 1});
                this.getWalletPendingTransactions({id: this.currentDepositAccountWallet.id, page: 1});

                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
        // Else create wallet transfer in FI instead
        else {
            await ajax.post(`/portal-backend/fi-api/wallets/${this.currentDepositAccountWallet.id}/transfer`, request)
            .then(response => {
                this.context.commit("setDepositAccountWalletsAfterTransfer", response.data.sourceWallet);
                this.context.commit("setDepositAccountWalletsAfterTransfer", response.data.destinationWallet);

                // Only get an updated list of transactions after the wallet transfer returns successfully
                this.getWalletTransactions({id: this.currentDepositAccountWallet.id, page: 1});
                this.getWalletPendingTransactions({id: this.currentDepositAccountWallet.id, page: 1});

                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
    }

    @Action
    async updateAccountWalletDetails(request: UpdateWalletDetailsRequest) {

        this.setLoadingDialog(true);

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.patch(`/portal-backend/ux-api/accounts/${this.depositAccount.id}/wallets/${this.currentDepositAccountWallet.id}`, request)
            .then(response => {
                this.context.commit("setDepositAccountWallet", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
        // Else update wallet in FI instead
        else {
            await ajax.patch(`/portal-backend/fi-api/wallets/${this.currentDepositAccountWallet.id}`, request)
            .then(response => {
                this.context.commit("setDepositAccountWallet", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
    }

    @Action
    async updateAccountSecondaryWalletInterestSettings(request: UpdateSecondaryWalletInterestSettingsRequest) {

        this.setLoadingDialog(true);
        request.rateValue = +request.rateValue;

        await ajax.put(`/portal-backend/fi-api/internal/wallets/${this.currentDepositAccountWallet.id}/deposit-interest-settings`, request)
        .then(response => {
            this.context.commit("setDepositAccountWallet", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async deleteAccountSecondaryWalletInterestSettings() {

        this.setLoadingDialog(true);

        await ajax.delete(`/portal-backend/fi-api/internal/wallets/${this.currentDepositAccountWallet.id}/deposit-interest-settings`)
        .then(response => {
            this.context.commit("setDepositAccountWallet", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getWalletDetails(walletID: string) {

        this.setLoadingDialog(true);

        await ajax.get(`/portal-backend/fi-api/wallets/${walletID}`)
        .then(response => {
            this.context.commit("setCurrentDepositAccountWallet", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getWalletInterestAccrual(walletID: string) {
        // TODO: Remove this once the getWalletDetails has been updated to return interest accrual
        await ajax.get(`/portal-backend/fi-api/internal/wallets-accrual/${walletID}`)
        .then(response => {
            this.context.commit("setCurrentDepositAccountWallet", response.data);
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async getWalletTransactions(data: {id: string, page: number}) {

        this.setLoadingTransactions(true);

        const orderParameter = this.currentDepositAccountWalletTransactionsOrder ? `&orderBy=${this.currentDepositAccountWalletTransactionsOrder}` : '';
        const searchParameter = this.currentDepositAccountWalletTransactionsSearch ? `&reference=${ValidationUtils.encodeRFC5987ValueChars(this.currentDepositAccountWalletTransactionsSearch)}` : '';

        await ajax.get(`/portal-backend/fi-api/accounts/${this.depositAccount.id}/wallets/${data.id}/transactions?order=DESC&pageSize=${this.pageSize}&pageNumber=${data.page}${searchParameter}${orderParameter}`)
        .then(response => {
            this.context.commit("setDepositAccountWalletTransactions", response.data.transactions);
            this.context.commit("setDepositAccountWalletTransactionsTotalPages", response.data.records.totalRecords);
            this.setLoadingTransactions(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingTransactions(false);
        });
    }

    @Action
    async getWalletPendingTransactions(data: {id: string, page: number}) {

        this.setLoadingPendingTransactions(true);

        const orderParameter = this.currentDepositAccountWalletTransactionsOrder ? `&orderBy=${this.currentDepositAccountWalletTransactionsOrder}` : '';
        const searchParameter = this.currentDepositAccountWalletTransactionsSearch ? `&reference=${ValidationUtils.encodeRFC5987ValueChars(this.currentDepositAccountWalletTransactionsSearch)}` : '';

        await ajax.get(`/portal-backend/fi-api/accounts/${this.depositAccount.id}/wallets/${data.id}/pendingTransactions?order=DESC&pageSize=${this.pageSize}&pageNumber=${data.page}${searchParameter}${orderParameter}`)
        .then(response => {
            this.context.commit("setDepositAccountWalletPendingTransactions", response.data.pendingTransactions);
            this.context.commit("setDepositAccountWalletPendingTransactionsTotalPages", response.data.records.totalRecords);
            this.setLoadingPendingTransactions(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPendingTransactions(false);
        });
    }

    @Action
    async getWalletTransactionRichData(transactionId: string) {

        this.setLoadingTransactions(true);

        await ajax.get(`/portal-backend/fi-api/accounts/${this.depositAccount.id}/wallets/${this.currentDepositAccountWallet.id}/transactions/${transactionId}`)
        .then(response => {

            // We need to parse the rich data because it is returned as a string and we want to
            // display it as JSON - we do this here instead of the UI incase the parse fails, and if there is an
            // error then display the error inside the rich data so we can quickly trace the problem later
            let transactionData = response.data.transaction;
            if (typeof transactionData.richdata === 'string') {
                try {
                    transactionData.richdata = JSON.parse(transactionData.richdata);
                }
                catch (error) {
                    transactionData.richdata = {parseError: `${error}`};
                }
            }

            // Set the data once we have finished parsing it
            this.context.commit("setDepositAccountWalletTransactionRichData", transactionData);
            this.setLoadingTransactions(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingTransactions(false);
        });
    }


    @Action
    async getWalletPendingTransactionRichData(transactionId: string) {

        this.setLoadingTransactions(true);

        await ajax.get(`/portal-backend/fi-api/accounts/${this.depositAccount.id}/wallets/${this.currentDepositAccountWallet.id}/pendingTransactions/${transactionId}`)
        .then(response => {

            // We need to parse the rich data because it is returned as a string and we want to
            // display it as JSON - we do this here instead of the UI incase the parse fails, and if there is an
            // error then display the error inside the rich data so we can quickly trace the problem later
            let pendingTransactionData = response.data.pendingTransaction;
            if (typeof pendingTransactionData.richdata === 'string') {
                try {
                    pendingTransactionData.richdata = JSON.parse(pendingTransactionData.richdata);
                }
                catch (error) {
                    pendingTransactionData.richdata = {parseError: `${error}`};
                }
            }

            // Set the data once we have finished parsing it
            this.context.commit("setDepositAccountWalletTransactionRichData", pendingTransactionData);
            this.setLoadingTransactions(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingTransactions(false);
        });
    }

    @Action
    async updateCurrentDepositAccountWalletTransactionsOrder(order: TransactionsOrderOptions) {
        this.context.commit("setCurrentDepositAccountWalletTransactionsOrder", order);
    }

    @Action
    async updateCurrentDepositAccountWalletTransactionsSearch(searchText: string) {
        this.context.commit("setCurrentDepositAccountWalletTransactionsSearch", searchText);
    }

    @Action
    async releasePendingTransaction(transactionID: string) {

        this.setLoadingPendingTransactions(true);

        await ajax.delete(`/portal-backend/fi-api/accounts/${this.depositAccount.id}/pendingTransactions/${transactionID}`)
        .then(() => {
            this.setLoadingPendingTransactions(false);
            // Then get the latest transactions to see the newly released pending transaction
            this.getWalletTransactions({id: this.currentDepositAccountWallet.id, page: 1});
            this.getWalletPendingTransactions({id: this.currentDepositAccountWallet.id, page: 1});
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPendingTransactions(false);
        });
    }

    @Action
    async confirmWalletBuySellFX(buySellFXRequest: BuySellFXRequest) {

        // There are three requests needed in this flow, a buy, a sell and and a fee
        // So we make all three requests based on the information collectively passed in

        const sellRequest = new AdjustmentRequest();
        sellRequest.accountId = this.depositAccount.id;
        sellRequest.walletId = buySellFXRequest.sourceWalletId;
        sellRequest.amount = new Amount();
        sellRequest.amount.currency = buySellFXRequest.sourceAmount.currency;
        sellRequest.amount.amount = CurrencyUtils.convertDecimalStringToAmountString(buySellFXRequest.sourceAmount.amount);
        sellRequest.reference = `${buySellFXRequest.sellCurrency} sold for ${buySellFXRequest.buyCurrency}`;
        sellRequest.debitCreditCode = DebitCreditCode.debit;
        sellRequest.transactionType = TransactionType.domesticMotoFx;

        await ajax.post(`/portal-backend/fi-api/adjustment`, sellRequest)
        .then(response => {
            // TODO: Update the list of wallet transactions
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });

        const feeRequest = new AdjustmentRequest();
        feeRequest.accountId = this.depositAccount.id;
        feeRequest.walletId = buySellFXRequest.sourceWalletId;
        feeRequest.amount = new Amount();
        feeRequest.amount.currency = buySellFXRequest.feeAmount.currency;
        feeRequest.amount.amount = CurrencyUtils.convertDecimalStringToAmountString(buySellFXRequest.feeAmount.amount);
        feeRequest.reference = `FX Fee`;
        feeRequest.debitCreditCode = DebitCreditCode.debit;
        feeRequest.transactionType = TransactionType.cardSchemeInterchangeFee;

        await ajax.post(`/portal-backend/fi-api/adjustment`, feeRequest)
        .then(response => {
            // TODO: Update the list of wallet transactions
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });

        const buyRequest = new AdjustmentRequest();
        buyRequest.accountId = this.depositAccount.id;
        buyRequest.walletId = buySellFXRequest.destinationWalletId;
        buyRequest.amount = new Amount();
        buyRequest.amount.currency = buySellFXRequest.destinationAmount.currency;
        buyRequest.amount.amount = CurrencyUtils.convertDecimalStringToAmountString(buySellFXRequest.destinationAmount.amount);
        buyRequest.reference = `${buySellFXRequest.buyCurrency} purchased with ${buySellFXRequest.sellCurrency}`;
        buyRequest.debitCreditCode = DebitCreditCode.credit;
        buyRequest.transactionType = TransactionType.domesticMotoFx;

        await ajax.post(`/portal-backend/fi-api/adjustment`, buyRequest)
        .then(response => {
            // TODO: Update the list of wallet transactions
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async getProductOptionsForDepositAccount() {

        const productType = DepositAccountSettingsModule.productIssue.generalSettings.type;
        const productSubType = DepositAccountSettingsModule.productIssue.generalSettings.subType;

        await ajax.get(`/portal-backend/fi-api/internal/products?type=${productType}&subType=${productSubType}`)
        .then(response => {
            this.context.commit("setProductOptions", response.data.results);
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async getProductIssueOptionsForDepositAccount(productID: string) {
        await ajax.get(`/portal-backend/fi-api/internal/product-issues?productId=${productID}`)
        .then(response => {
            this.context.commit("setProductIssueOptions", response.data.results);
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async createCard(request: CreateCardRequest) {

        this.setLoadingDialog(true);

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.post(`/portal-backend/ux-api/cards`, request)
            .then(response => {
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
        // Else create card using FI instead
        else {
            await ajax.post(`/portal-backend/fi-api/card-interfaces`, request)
            .then(response => {
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
    }

    @Action
    async getCards(pageNumber: number) {

        this.setLoadingCards(true);

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.get(`/portal-backend/ux-api/accounts/${this.depositAccount.id}/cards?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
            .then(response => {
                this.context.commit("setCards", response.data.results);
                this.context.commit("setCardsTotalPages", response.data.records.totalRecords);
                if (response.data.results.length > 0) {
                    this.context.commit("setCurrentCard", response.data.results[0]);
                }
                this.setLoadingCards(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingCards(false);
            });
        }
        // Else get cards from FI instead
        else {
            await ajax.get(`/portal-backend/fi-api/accounts/${this.depositAccount.id}/card-interfaces?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
            .then(response => {
                this.context.commit("setCards", response.data.results);
                this.context.commit("setCardsTotalPages", response.data.records.totalRecords);
                if (response.data.results.length > 0) {
                    this.context.commit("setCurrentCard", response.data.results[0]);
                }
                this.setLoadingCards(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingCards(false);
            });
        }
    }

    @Action
    async editCard(request: UpdateCardRequest) {

        this.setLoadingDialog(true);

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.patch(`/portal-backend/ux-api/cards/${this.currentCard.id}`, request)
            .then(response => {
                this.context.commit("setCurrentCard", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
        // Else edit card using FI instead
        else {
            await ajax.patch(`/portal-backend/fi-api/card-interfaces/${this.currentCard.id}`, request)
            .then(response => {
                this.context.commit("setCurrentCard", response.data);
                this.setLoadingDialog(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingDialog(false);
            });
        }
    }

    @Action
    public async updateCurrentCard(card: Card){
        this.context.commit("setCurrentCard", card);
    }

    @Action
    async deleteCard(id: string) {

        this.setLoadingCards(true);

        // If UX API Access is allowed, use UX API
        if (AuthModule.me.institution.config.uxApiAccess && PermissionUtils.allowUxApiAccess()) {
            await ajax.delete(`/portal-backend/ux-api/cards/${id}`)
            .then(response => {
                this.setLoadingCards(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingCards(false);
            });
        }
        // Else delete carde from FI instead
        else {
            await ajax.delete(`/portal-backend/fi-api/card-interfaces/${id}`)
            .then(response => {
                this.setLoadingCards(false);
            })
            .catch(error => {
                AppModule.handleApiError(error);
                this.setLoadingCards(false);
            });
        }
    }

    @Action
    async createPayee(request: CreatePayeeRequest) {

        this.setLoadingDialog(true);
        request.accountType = AccountType.Customer;

        await ajax.post(`/portal-backend/fi-api/payees`, request)
        .then(response => {
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getPayees(pageNumber: number) {

        this.setLoadingPayees(true);

        await ajax.get(`/portal-backend/fi-api/accounts/${this.depositAccount.id}/payees?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
        .then(response => {
            this.context.commit("setPayees", response.data.results);
            this.context.commit("setPayeesTotalPages", response.data.records.totalRecords);
            if (response.data.results.length > 0) {
                this.context.commit("setCurrentPayee", response.data.results[0]);
            }
            this.setLoadingPayees(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPayees(false);
        });
    }

    @Action
    async getPayeesForDialog() {

        // This action is specifically for the edit/create dialogs where we need to ignore the normal API pagination
        // logic in getPayees and instead get all payees so they can be listed in the drop down menu for dialogs
        this.setLoadingPayees(true);

        await ajax.get(`/portal-backend/fi-api/accounts/${this.depositAccount.id}/payees`)
        .then(response => {
            this.context.commit("setPayeesForDialog", response.data.results);
            this.setLoadingPayees(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPayees(false);
        });
    }

    @Action
    async deletePayee() {

        this.setLoadingPayees(true);

        await ajax.delete(`/portal-backend/fi-api/payees/${this.currentPayee.id}`)
        .then(() => {
            this.setLoadingPayees(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPayees(false);
        });
    }

    @Action
    public async updateCurrentPayee(payee: Payee){
        this.context.commit("setCurrentPayee", payee);
    }

    @Action
    async createPaymentOrder(request: CreatePaymentOrderRequest) {

        this.setLoadingDialog(true);

        await ajax.post(`/portal-backend/fi-api/payment-orders`, request)
        .then(response => {
            this.context.commit("setPaymentOrderFromUpdate", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async createHawkAIPaymentOrder(request: CreatePaymentOrderRequest) {

        this.setLoadingDialog(true);

        await ajax.post(`/portal-backend/fi-api/hawk-payment-orders`, request)
        .then(response => {
            // If request from HawkAI was successful then save the payment order
            if (response.data.proceed) {
                this.context.commit("setPaymentOrderFromUpdate", response.data);
            }
            // Otherwise it has been blocked so show an error
            else {
                AppModule.setSimpleErrorMessage("Transaction has been blocked");
            }
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async approvePaymentOrder() {

        this.setLoadingDialog(true);

        await ajax.post(`/portal-backend/fi-api/payment-orders/${this.currentPaymentOrder.id}/approve`)
        .then(response => {
            this.context.commit("setPaymentOrderFromUpdate", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async cancelPaymentOrder(request: CancelRejectPaymentOrderRequest) {

        this.setLoadingDialog(true);

        await ajax.post(`/portal-backend/fi-api/payment-orders/${this.currentPaymentOrder.id}/cancel`, request)
        .then(response => {
            this.context.commit("setPaymentOrderFromUpdate", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async rejectPaymentOrder(request: CancelRejectPaymentOrderRequest) {

        this.setLoadingDialog(true);

        await ajax.post(`/portal-backend/fi-api/payment-orders/${this.currentPaymentOrder.id}/reject`, request)
        .then(response => {
            this.context.commit("setPaymentOrderFromUpdate", response.data);
            this.setLoadingDialog(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingDialog(false);
        });
    }

    @Action
    async getPaymentOrders(pageNumber: number) {

        this.setLoadingPaymentOrders(true);

        await ajax.get(`/portal-backend/fi-api/accounts/${this.depositAccount.id}/payment-orders?pageSize=${this.pageSize}&pageNumber=${pageNumber}`)
        .then(response => {
            this.context.commit("setPaymentOrders", response.data.results);
            this.context.commit("setPaymentOrdersTotalPages", response.data.records.totalRecords);
            if (response.data.results.length > 0) {
                this.context.commit("setCurrentPaymentOrder", response.data.results[0]);
            }
            this.setLoadingPaymentOrders(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPaymentOrders(false);
        });
    }

    @Action
    public async updateCurrentPaymentOrder(paymentOrder: PaymentOrder){
        this.context.commit("setCurrentPaymentOrder", paymentOrder);
    }

    @Action
    async getPaymentsForPaymentOrder() {

        this.setLoadingPaymentsForPaymentOrder(true);

        await ajax.get(`/portal-backend/fi-api/payment-orders/${this.currentPaymentOrder.id}`)
        .then(response => {
            this.context.commit("setPaymentsForPaymentOrder", response.data.payments);
            this.setLoadingPaymentsForPaymentOrder(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPaymentsForPaymentOrder(false);
        });
    }

    @Action
    async getPayments(pageNumber: number) {

        this.setLoadingPayments(true);

        await ajax.get(`/portal-backend/fi-api/accounts/${this.depositAccount.id}/payments?pageSize=${this.pageSize}&pageNumber=${pageNumber}&state=${this.filterByPaymentState}`)
        .then(response => {
            this.context.commit("setPayments", response.data.results);
            this.context.commit("setPaymentsTotalPages", response.data.records.totalRecords);
            if (response.data.results.length > 0) {
                this.context.commit("setCurrentPayment", response.data.results[0]);
            }
            this.setLoadingPayments(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPayments(false);
        });
    }

    @Action
    async cancelPayment(paymentID: string) {

        this.setLoadingPayments(true);

        await ajax.delete(`/portal-backend/fi-api/payments/${paymentID}`)
        .then(response => {

            // Work out the updated list of payments by filtering out payment that was just deleted
            const updatedPayments = this.payments.filter((payment) => { return payment.id !== paymentID });

            // Reset the payments and current payment with the newly updated list
            this.context.commit("setPayments", updatedPayments);
            this.context.commit("setPaymentsTotalPages", updatedPayments.length);
            if (updatedPayments.length > 0) {
                this.context.commit("setCurrentPayment", updatedPayments[0]);
            }
            else {
                const emptyPayment = new Payment();
                this.context.commit("setCurrentPayment", emptyPayment);
            }

            this.setLoadingPayments(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPayments(false);
        });
    }

    @Action
    public async updateCurrentPayment(payment: Payment){
        this.context.commit("setCurrentPayment", payment);
    }

    @Action
    async cancelAllPaymentsInPaymentOrder(paymentOrderID: string) {

        this.setLoadingPayments(true);

        await ajax.delete(`/portal-backend/fi-api/payment-orders/${paymentOrderID}/payments`)
        .then(response => {
            this.setLoadingPayments(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingPayments(false);
        });
    }

    @Action
    public async clearPaymentsForPaymentOrder(){
        this.context.commit("setPaymentsForPaymentOrder", []);
    }

    @Action
    async updateFilterByPaymentState(val: PaymentState) {
        this.context.commit("setFilterByPaymentState", val);
    }

    @Action
    async getMandates() {

        this.setLoadingMandates(true);

        await ajax.get(`/portal-backend/fi-api/accounts/${this.depositAccount.id}/mandates`)
        .then(response => {
            this.context.commit("setMandates", response.data.directDebitMandates);
            this.context.commit("setMandatesTotalPages", response.data.records.totalRecords);
            if (response.data.directDebitMandates.length > 0) {
                this.context.commit("setMandate", response.data.directDebitMandates[0]);
            }
            this.setLoadingMandates(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingMandates(false);
        });
    }

    @Action
    updateMandate(mandate: Mandate) {
        this.context.commit("setMandate", mandate);
    }

    @Action
    async getFees() {

        this.setLoadingFees(true);

        await ajax.get(`/portal-backend/fi-api/fees/?accountId=${this.depositAccount.id}`)
        .then(response => {
            this.context.commit("setFees", response.data.fees);
            this.context.commit("setFeesTotalPages", response.data.records.totalRecords);
            if (response.data.fees.length > 0) {
                this.context.commit("setCurrentFee", response.data.fees[0]);
            }
            this.setLoadingFees(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingFees(false);
        });
    }

    @Action
    async createFee(request: CreateFeeRequest) {

        this.setLoadingFees(true);
        request.amount.amount = CurrencyUtils.convertDecimalStringToAmountString(request.amount.amount);

        await ajax.post(`/portal-backend/fi-api/fees/`, request)
        .then(response => {
            this.context.commit("setFeeFromCreate", response.data);
            this.setLoadingFees(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingFees(false);
        });
    }

    @Action
    public async updateCurrentFee(fee: Fee){
        this.context.commit("setCurrentFee", fee);
    }

    @Action
    async manuallyPostInterest(request: ManuallyPostInterestRequest) {

        // Manually posting interest uses different endpoints depending on the product type and the wallet type:
        // 1 - Fixed term deposit products
        // 2 - Default wallets with an overdraft
        // 3 - Secondary wallets with accrued savings interest

        if (this.depositAccount.productSubType === ProductSubType.DepositFixedTerm) {
            await ajax.post(`/portal-backend/fi-api/internal/accrual/fixed-term-deposit-posting`, request)
            .then(response => {
                // After manually posting interest, get the wallet interest accrual info for the current wallet
                this.getWalletInterestAccrual(this.currentDepositAccountWallet.id);
                // Then get the latest transactions to get the posted interest transactions
                this.getWalletTransactions({id: this.currentDepositAccountWallet.id, page: 1});
                this.getWalletPendingTransactions({id: this.currentDepositAccountWallet.id, page: 1});
            })
            .catch(error => {
                AppModule.handleApiError(error);
            });
        }
        else {
            if (this.currentDepositAccountWallet.default) {
                await ajax.post(`/portal-backend/fi-api/internal/accrual/overdraft-posting`, request)
                .then(response => {
                    // After manually posting interest, get the wallet interest accrual info for the current wallet
                    this.getWalletInterestAccrual(this.currentDepositAccountWallet.id);
                    // Then get the latest transactions to get the posted interest transactions
                    this.getWalletTransactions({id: this.currentDepositAccountWallet.id, page: 1});
                    this.getWalletPendingTransactions({id: this.currentDepositAccountWallet.id, page: 1});
                })
                .catch(error => {
                    AppModule.handleApiError(error);
                });
            }
            else {
                // For secondary wallets, also pass in the wallet ID so we only post interest on the one wallet that is being viewed
                request.walletId = this.currentDepositAccountWallet.id;

                await ajax.post(`/portal-backend/fi-api/internal/accrual/secondary-wallet-posting`, request)
                .then(response => {
                    // After manually posting interest, get the wallet interest accrual info for the current wallet
                    this.getWalletInterestAccrual(this.currentDepositAccountWallet.id);
                    // Then get the latest transactions to get the posted interest transactions
                    this.getWalletTransactions({id: this.currentDepositAccountWallet.id, page: 1});
                    this.getWalletPendingTransactions({id: this.currentDepositAccountWallet.id, page: 1});
                })
                .catch(error => {
                    AppModule.handleApiError(error);
                });
            }
        }
    }

    @Action
    async makeAdjustment(adjustmentRequest: AdjustmentRequest) {
        await ajax.post(`/portal-backend/fi-api/adjustment`, adjustmentRequest)
        .then(response => {
            // TODO: Update the list of wallet transactions
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async rollForwardAccountDate(rollForwardAccountDateRequest: RollForwardAccountDateRequest) {
        await ajax.post(`/portal-backend/fi-api/internal/accounts/roll-forward-account-date`, rollForwardAccountDateRequest)
        .then(response => {
            // After rolling forward the date, get the deposit account information again
            this.getDepositAccount(this.depositAccount.id);
        })
        .catch(error => {
            AppModule.handleApiError(error);
        });
    }

    @Action
    async getHistory() {

        this.setLoadingHistory(true);

        await ajax.get(`/portal-backend/fi-api/internal/accounts/${this.depositAccount.id}/history`)
        .then(response => {
            this.context.commit("setHistory", response.data);
            this.setLoadingHistory(false);
        })
        .catch(error => {
            AppModule.handleApiError(error);
            this.setLoadingHistory(false);
        });
    }

    @Action
    public async cleanState() {
        this.context.commit("resetState");
    }

    @Mutation
    resetState() {
        this.loadingDepositAccounts = true;
        this.loadingDepositAccount = true;
        this.loadingWallets = true;
        this.loadingCards = true;
        this.loadingDialog = false;
        this.loadingHistory = true;
        this.loadingTransactions = true;
        this.loadingPendingTransactions = true;
        this.loadingPayees = true;
        this.loadingPaymentOrders = true;
        this.loadingPaymentsForPaymentOrder = true;
        this.loadingPayments = true;
        this.loadingMandates = true;
        this.loadingFees = true;
        this.depositAccount = new DepositAccount();
        this.depositAccountsSearch = '';
        this.depositAccountsSearchType = AccountSearchType.AccountName;
        this.totalDepositAccountsPages = 1;
        this.depositAccounts = [];
        this.currentDepositAccountWallet = new Wallet();
        this.currentDepositAccountWalletTransactions = [];
        this.currentDepositAccountWalletTransactionRichData = new Transaction();
        this.totalDepositAccountWalletTransactionsPages = 1;
        this.currentDepositAccountWalletPendingTransactions = [];
        this.totalDepositAccountWalletPendingTransactionsPages = 1;
        this.currentDepositAccountWalletTransactionsOrder = TransactionsOrderOptions.DisplayDate;
        this.currentDepositAccountWalletTransactionsSearch = '';
        this.cards = [];
        this.totalCardsPages = 1;
        this.currentCard = new Card();
        this.payees = [];
        this.payeesForDialog = [];
        this.totalPayeesPages = 1;
        this.currentPayee = new Payee();
        this.paymentOrders = [];
        this.totalPaymentOrdersPages = 1;
        this.currentPaymentOrder = new PaymentOrder();
        this.paymentsForPaymentOrder = [];
        this.payments = [];
        this.totalPaymentsPages = 1;
        this.currentPayment =  new Payment();
        this.filterByPaymentState = PaymentState.Completed;
        this.mandates = [];
        this.mandate = new Mandate();
        this.totalMandatePages = 1;
        this.fees = [];
        this.totalFeesPages = 1;
        this.currentFee = new Fee();
        this.productOptions = [];
        this.productIssueOptions = [];
    }

    @Mutation
    private async setLoadingDepositAccounts(loading: boolean) {
        this.loadingDepositAccounts = loading;
    }

    @Mutation
    private async setLoadingDepositAccount(loading: boolean) {
        this.loadingDepositAccount = loading;
    }

    @Mutation
    private async setLoadingCards(loading: boolean) {
        this.loadingCards = loading;
    }

    @Mutation
    private async setLoadingDialog(loading: boolean) {
        this.loadingDialog = loading;
    }

    @Mutation
    private async setLoadingHistory(loading: boolean) {
        this.loadingHistory = loading;
    }

    @Mutation
    private async setLoadingTransactions(loading: boolean) {
        this.loadingTransactions = loading;
    }

    @Mutation
    private async setLoadingPendingTransactions(loading: boolean) {
        this.loadingPendingTransactions = loading;
    }

    @Mutation
    private async setLoadingPaymentOrders(loading: boolean) {
        this.loadingPaymentOrders = loading;
    }

    @Mutation
    private async setLoadingPayees(loading: boolean) {
        this.loadingPayees = loading;
    }

    @Mutation
    private async setLoadingPaymentsForPaymentOrder(loading: boolean) {
        this.loadingPaymentsForPaymentOrder = loading;
    }

    @Mutation
    private async setLoadingPayments(loading: boolean) {
        this.loadingPayments = loading;
    }

    @Mutation
    private async setLoadingMandates(loading: boolean) {
        this.loadingMandates = loading;
    }

    @Mutation
    private async setLoadingFees(loading: boolean) {
        this.loadingFees = loading;
    }

    @Mutation
    private async setDepositAccounts(accounts: DepositAccountSummary[]) {
        if (accounts) {
            this.depositAccounts = accounts;
        }
    }

    @Mutation
    private async resetDepositAccounts() {
        this.depositAccounts = [];
    }

    @Mutation
    private async setDepositAccountsSearch(text: string) {
        this.depositAccountsSearch = text;
    }

    @Mutation
    private async setDepositAccountsSearchType(type: AccountSearchType) {
        this.depositAccountsSearchType = type;
    }

    @Mutation
    private async setDepositAccountsTotalPages(total: number) {
        this.totalDepositAccountsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setDepositAccount(response: DepositAccount) {
        this.depositAccount = response;
    }

    @Mutation
    private async setDepositAccountWallet(response: Wallet) {
        const walletIndex = this.depositAccount.wallets.findIndex(wallet => wallet.id === response.id);
        // We have to use .splice so that vue picks up the change and refreshes the UI (vue2 only problem with reactivity, fixed in vue3)
        if (walletIndex >= 0) {
            this.depositAccount.wallets.splice(walletIndex, 1, response);
            this.currentDepositAccountWallet = response;
        }
        else {
            this.depositAccount.wallets.push(response);
        }
    }

    // NOTE: similar to setDepositAccountWallet we have to use .splice for vue to pick up the changes but
    // it still doesn't work, so this will correctly updated the vuex state but not refresh the UI
    // https://michaelnthiessen.com/debugging-guide-why-your-component-isnt-updating/
    // TODO: Leaving this in as tech debt until we update to vue3
    @Mutation
    private async setDepositAccountWalletsAfterTransfer(response: Wallet) {
        const walletIndex = this.depositAccount.wallets.findIndex(wallet => wallet.id === response.id);
        // Take the exisiting wallet data and update the balances with new data
        const updatedWallet = this.depositAccount.wallets[walletIndex];
        updatedWallet.availableBalance = response.availableBalance;
        updatedWallet.balance = response.balance;
        // We have to use .set so that vue picks up the change and refreshes the UI
        this.depositAccount.wallets.splice(walletIndex, 1, updatedWallet);
    }

    @Mutation
    private async setCurrentDepositAccountWallet(response: Wallet) {
        this.currentDepositAccountWallet = response;
    }

    @Mutation
    private async setDepositAccountWalletTransactions(transactions: Transaction[]) {
        if (transactions) {
            this.currentDepositAccountWalletTransactions = transactions;
        }
    }

    @Mutation
    private async setDepositAccountWalletTransactionRichData(transaction: Transaction) {
        if (transaction) {
            this.currentDepositAccountWalletTransactionRichData = transaction;
        }
    }

    @Mutation
    private async setDepositAccountWalletTransactionsTotalPages(total: number) {
        this.totalDepositAccountWalletTransactionsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setDepositAccountWalletPendingTransactions(transactions: Transaction[]) {
        if (transactions) {
            this.currentDepositAccountWalletPendingTransactions = transactions;
        }
    }

    @Mutation
    private async setDepositAccountWalletPendingTransactionsTotalPages(total: number) {
        this.totalDepositAccountWalletPendingTransactionsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setCurrentDepositAccountWalletTransactionsOrder(order: TransactionsOrderOptions) {
        this.currentDepositAccountWalletTransactionsOrder = order;
    }

    @Mutation
    private async setCurrentDepositAccountWalletTransactionsSearch(text: string) {
        this.currentDepositAccountWalletTransactionsSearch = text;
    }

    @Mutation
    private async setCards(response: Card[]) {
        if (response) {
            this.cards = response;
        }
    }

    @Mutation
    private async setCardsTotalPages(total: number) {
        this.totalCardsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setCurrentCard(card: Card) {
        this.currentCard = card;
    }

    @Mutation
    private async setPayees(response: Payee[]) {
        if (response) {
            this.payees = response;
        }
    }

    @Mutation
    private async setPayeesForDialog(response: Payee[]) {
        this.payeesForDialog = response;
    }

    @Mutation
    private async setPayeesTotalPages(total: number) {
        this.totalPayeesPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setCurrentPayee(payee: Payee) {
        this.currentPayee = payee;
    }

    @Mutation
    private async setPaymentOrders(response: PaymentOrder[]) {
        if (response) {
            this.paymentOrders = response;
        }
    }

    @Mutation
    private async setPaymentOrdersTotalPages(total: number) {
        this.totalPaymentOrdersPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setCurrentPaymentOrder(paymentOrder: PaymentOrder) {
        this.currentPaymentOrder = paymentOrder;
    }

    @Mutation
    private async setPaymentOrderFromUpdate(paymentOrder: PaymentOrder) {
        this.currentPaymentOrder = paymentOrder;
        const index = this.paymentOrders.findIndex((findPO: PaymentOrder) => paymentOrder.id === findPO.id);
        this.paymentOrders.splice(index, 1, paymentOrder);
    }

    @Mutation
    private async setPaymentsForPaymentOrder(response: Payment[]) {
        if (response) {
            this.paymentsForPaymentOrder = response;
        }
    }

    @Mutation
    private async setPayments(response: Payment[]) {
        if (response) {
            this.payments = response;
        }
    }

    @Mutation
    private async setPaymentsTotalPages(total: number) {
        this.totalPaymentsPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setCurrentPayment(payment: Payment) {
        this.currentPayment = payment;
    }

    @Mutation
    private async setFilterByPaymentState(response: PaymentState) {
        this.filterByPaymentState = response;
    }

    @Mutation
    private async setMandates(response: Mandate[]) {
        if (response) {
            this.mandates = response;
        }
    }

    @Mutation
    private async setMandate(response: Mandate) {
        this.mandate = response;
    }

    @Mutation
    private async setMandatesTotalPages(total: number) {
        this.totalMandatePages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setFees(response: Fee[]) {
        if (response) {
            this.fees = response;
        }
    }

    @Mutation
    private async setFeesTotalPages(total: number) {
        this.totalFeesPages = Math.ceil(total/this.pageSize);
    }

    @Mutation
    private async setFeeFromCreate(response: Fee) {
        this.fees.push(response);
    }

    @Mutation
    private async setCurrentFee(fee: Fee) {
        this.currentFee = fee;
    }

    @Mutation
    private async setProductOptions(response: Product[]) {
        if (response) {
            this.productOptions = response;
        }
    }

    @Mutation
    private async setProductIssueOptions(response: ProductIssue[]) {
        if (response) {
            this.productIssueOptions = response;
        }
    }

    @Mutation
    private async setHistory(response: GetHistoryResponse) {
        this.history = response.results;
    }

}

export const DepositAccountModule = getModule(DepositAccountStore);